import { zodResolver } from "@hookform/resolvers/zod";
import { Checkbox, FormHelperText, Icon, IconButton } from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiCard from "components/base/UiCard";
import UiCenterModal from "components/base/UiCenterModal";
import UiFieldGroup from "components/base/UiFieldGroup";
import UiInput from "components/base/UiInput";
import UiTypography from "components/base/UiTypography";
import CustomSelect from "components/Shared/CustomSelect/CustomSelect";
import CustomMultipleSelect from "components/Shared/CustomMultipleSelect/CustomMultipleSelect";
import WYSIWYG from "components/WYSIWYG/WYSIWYG";
import { Proposal, ProposalLayout, SendProposalMutationVariables } from "generated/graphql";
import useGetOrganization from "hooks/organization/useGetOrganization";
import useSendProposalPackage from "hooks/proposals/useSendProposalPackage";
import useSendProposal from "hooks/proposals/useSendProposal";
import useProposalPackageSummary from "hooks/proposals/useProposalPackageSummary";
import { useCallback, useContext, useMemo, useState } from "react";
import { Controller, FormProvider, useFieldArray, useForm, useFormContext } from "react-hook-form";
import { useNavigate } from "react-router";
import { enumToValueOptions } from "utils/enums/enumToValueOptions";
import { getRoute } from "utils/routing";
import { sentenceCase } from "change-case";
import { getOrganizationDocumentName } from "modules/organization/UpdateOrganization/DocumentItem";
import useGetOrganizationProposalContent from "hooks/organization/useGetorganizationProposalContent";
import { getMissingJobSizeMessage } from "modules/proposals/validators";
import { ProposalFormsContext } from "modules/proposals/providers/ProposalFormsContextProvider";
import useConfirmationDialog from "hooks/useConfirmationDialog";
import ActionConfirmDialog from "components/ActionConfirmDialog/ActionConfirmDialog";
import { proposalContentTypeName } from "constants/proposalSettingsDisplayType";
import { mapContentSettingsInput } from "DDD/action-objects/ProposalSend";
import {
  confirmProposalDropdownInnerSx,
  confirmProposalModalSchema,
  ConfirmProposalModalFormValues,
} from "./ConfirmProposalModal.shared";
import PackageRecipients from "./PackageRecipients";
import PreviewProposalButton from "./PreviewProposalButton";
import { draftColors } from "./draftDesignTokens";

type ConfirmProposalModalProps = {
  open: boolean;
  onClose: () => void;
  proposal: Proposal;
  initialAction: "preview" | "send";
};

const sectionHeaderSx = { color: "#7B809A" } as const;

function Recipients() {
  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext<ConfirmProposalModalFormValues>();
  const sendToMultipleCompanies = watch("sendToMultipleCompanies");

  const { fields, append, remove } = useFieldArray({
    control,
    name: "emails" as never,
  });

  const additionalFields = fields.slice(1);
  const firstAdditionalReg = register("emails.1");

  if (sendToMultipleCompanies) {
    return null;
  }

  return (
    <MDBox display="flex" flexDirection="column" gap="8px">
      <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap="12px">
        <UiFieldGroup label="Main contact recipient" value="" inputProps={register("emails.0")} />
        <UiFieldGroup label="Additional recipients" value="">
          <MDBox display="flex" alignItems="center" gap="4px">
            <UiInput
              fullWidth
              placeholder="Add recipient email"
              inputRef={firstAdditionalReg.ref}
              name={firstAdditionalReg.name}
              onChange={firstAdditionalReg.onChange}
              onBlur={firstAdditionalReg.onBlur}
            />
            <IconButton size="small" onClick={() => append("")} sx={{ p: "2px", flexShrink: 0 }}>
              <Icon sx={{ fontSize: "18px !important", color: draftColors.red }}>add</Icon>
            </IconButton>
          </MDBox>
        </UiFieldGroup>
      </MDBox>
      {additionalFields.slice(1).map((field, i) => {
        const fieldIndex = i + 2;
        return (
          <MDBox key={field.id} display="flex" alignItems="center" gap="4px">
            <MDBox flex={1}>
              <UiFieldGroup
                label="Additional recipient"
                value=""
                placeholder="Add recipient email"
                inputProps={register(`emails.${fieldIndex}`)}
              />
            </MDBox>
            <IconButton size="small" onClick={() => remove(fieldIndex)} sx={{ p: "2px" }}>
              <Icon sx={{ fontSize: "18px !important", color: draftColors.red }}>delete</Icon>
            </IconButton>
          </MDBox>
        );
      })}
      {errors.emails && (
        <FormHelperText sx={{ color: "red", mt: "-4px" }}>
          {errors.emails.message || "Please enter valid email addresses"}
        </FormHelperText>
      )}
    </MDBox>
  );
}

export default function ConfirmProposalModal({
  open,
  onClose,
  proposal,
  initialAction,
}: ConfirmProposalModalProps) {
  const navigate = useNavigate();
  const { data: orgData } = useGetOrganization();
  const { proposalContent } = useGetOrganizationProposalContent();
  const [sendProposal, sendProposalState] = useSendProposal({ id: proposal.id });
  const [sendProposalPackage, sendProposalPackageState] = useSendProposalPackage();
  const { data: packageSummaryData } = useProposalPackageSummary(proposal.id, !open);
  const { productsFormsStatus } = useContext(ProposalFormsContext);

  const contactName = proposal?.contact
    ? ((proposal.contact.firstName || "") + " " + (proposal.contact.lastName || "")).trim() || "N/A"
    : "N/A";

  const allDocuments = useMemo(() => {
    if (!orgData || !proposal) return [];
    return [...(orgData.documents ?? []), ...(proposal.attachments ?? [])];
  }, [orgData, proposal]);

  const getDocumentLabel = useCallback(
    (id: string) => {
      const document = allDocuments.find((d) => d.id === id);
      return getOrganizationDocumentName(document);
    },
    [allDocuments]
  );

  const defaultEmailBody = orgData?.proposalSentEmailText || "";
  const generatedSubject = `${proposal.company?.name ?? ""} Proposal for ${
    proposal.stages?.[0]?.name ?? ""
  }`;

  const proposalSectionOptions = useMemo(
    () =>
      (proposal.contentSettings ?? [])
        .filter((item) => item.contentType?.toLowerCase() !== "invoice_details")
        .map((item) => ({
          label:
            (proposalContentTypeName as Record<string, string>)[item.contentType.toUpperCase()] ??
            sentenceCase(item.contentType),
          value: item.id,
        })),
    [proposal.contentSettings]
  );

  const existingPackageRecipients = useMemo(() => {
    const packageChildren = (packageSummaryData?.getProposal as any)?.packageChildren ?? [];

    const uniqueRecipients = packageChildren.reduce((acc, child) => {
      const companyId = child?.company?.id;
      const contactId = child?.contact?.id;

      if (!companyId || !contactId) {
        return acc;
      }

      const key = `${companyId}:${contactId}`;
      if (acc.some((recipient) => `${recipient.companyId}:${recipient.contactId}` === key)) {
        return acc;
      }

      acc.push({ companyId, contactId });
      return acc;
    }, [] as Array<{ companyId: string; contactId: string }>);

    return uniqueRecipients;
  }, [packageSummaryData]);

  const defaultValues = useMemo(
    (): ConfirmProposalModalFormValues => ({
      proposalId: proposal.id,
      layout: (proposal.layout ?? ProposalLayout.SIMPLE) as ProposalLayout,
      sendToMultipleCompanies: existingPackageRecipients.length > 0,
      emails: [proposal.clientEmailOverride || proposal.contact?.email || "", ""],
      packageRecipients:
        existingPackageRecipients.length > 0
          ? existingPackageRecipients
          : [
              { companyId: "", contactId: "" },
              { companyId: "", contactId: "" },
            ],
      removeUnitPriceAndQuantity: proposal.removeUnitPriceAndQuantity ?? false,
      removeIndividualLineItemPricing: proposal.removeIndividualLineItemPricing ?? false,
      attachments: proposal.attachments?.map((at) => at.id) ?? [],
      contentSettingIds:
        proposal.contentSettings?.filter((item) => item.isEnabled).map((item) => item.id) ?? [],
      emailSubjectOverride: proposal.emailSubjectOverride || generatedSubject,
      emailBodyOverride: proposal.emailBodyOverride || defaultEmailBody,
      clientEmailOverride: proposal.clientEmailOverride ?? null,
      acceptanceOverride: proposal.acceptanceOverride ?? null,
    }),
    [defaultEmailBody, existingPackageRecipients, generatedSubject, proposal]
  );

  const methods = useForm<ConfirmProposalModalFormValues>({
    resolver: zodResolver(confirmProposalModalSchema),
    defaultValues,
    values: defaultValues,
  });

  const [jobSizeError, setJobSizeError] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [lastFormData, setLastFormData] = useState<ConfirmProposalModalFormValues | null>(null);

  const buildVariables = useCallback(
    (data: ConfirmProposalModalFormValues) => ({
      proposalId: data.proposalId,
      layout: data.layout,
      emails: data.emails.filter((e) => e.trim() !== ""),
      removeUnitPriceAndQuantity: data.removeUnitPriceAndQuantity,
      removeIndividualLineItemPricing: data.removeIndividualLineItemPricing,
      attachments: data.attachments ?? [],
      contentSettings: mapContentSettingsInput(proposal.contentSettings, data.contentSettingIds),
      emailSubjectOverride: data.emailSubjectOverride,
      emailBodyOverride: data.emailBodyOverride,
      clientEmailOverride: data.emails?.[0] ?? data.clientEmailOverride,
      acceptanceOverride: data.acceptanceOverride,
    }),
    [proposal.contentSettings]
  );

  const handleJobSizeError = useCallback((result: any) => {
    if (
      result?.name === "ApolloError" &&
      result?.message?.indexOf("Please input a job size.") !== -1
    ) {
      setJobSizeError(result.message);
      setConfirmDialogOpen(true);
    }
  }, []);

  const handleSend = useCallback(
    async (data: ConfirmProposalModalFormValues, forceSend?: boolean) => {
      if (!forceSend) {
        const msg = getMissingJobSizeMessage(proposal);
        if (msg) {
          setLastFormData(data);
          setJobSizeError(msg);
          setConfirmDialogOpen(true);
          return;
        }
      }

      if (data.sendToMultipleCompanies) {
        const result = await sendProposalPackage({
            variables: {
              proposalId: data.proposalId,
              recipients: data.packageRecipients
                .filter((recipient) => recipient.companyId && recipient.contactId)
                .map((recipient) => ({
                  companyId: recipient.companyId,
                  contactId: recipient.contactId,
                })),
              layout: data.layout,
              removeUnitPriceAndQuantity: data.removeUnitPriceAndQuantity,
              removeIndividualLineItemPricing: data.removeIndividualLineItemPricing,
              proposalNotes: proposal.proposalNotes,
              attachments: data.attachments ?? [],
              contentSettings: mapContentSettingsInput(
                proposal.contentSettings,
                data.contentSettingIds
              ),
              emailSubjectOverride: data.emailSubjectOverride,
              emailBodyOverride: data.emailBodyOverride,
              acceptanceOverride: data.acceptanceOverride,
            },
          });

        if (sendProposalPackageState.isSuccess(result.data)) {
          onClose();
          navigate(getRoute("proposals.info", [["proposalId", proposal.id]]));
        } else {
          handleJobSizeError(result);
        }
      } else {
        const result = await sendProposal({
          variables: {
            ...buildVariables(data),
            ...(forceSend && { forceSend: true }),
          } as SendProposalMutationVariables,
        });

        if (sendProposalState.isSuccess(result.data)) {
          onClose();
          navigate(getRoute("proposals.info", [["proposalId", proposal.id]]));
        } else {
          handleJobSizeError(result);
        }
      }
    },
    [
      buildVariables,
      handleJobSizeError,
      navigate,
      onClose,
      proposal,
      sendProposal,
      sendProposalPackageState,
      sendProposalState,
      sendProposalPackage,
    ]
  );

  const buildPreviewVarsFromForm = useCallback(() => {
    const data = methods.getValues();
    return buildVariables(data);
  }, [methods, buildVariables]);

  const acceptFn = useCallback(async () => {
    if (!lastFormData) return true;
    await handleSend(lastFormData, true);
    return true;
  }, [lastFormData, handleSend]);

  const cancelFn = useCallback(async () => true, []);

  const { onAccept, onCancel } = useConfirmationDialog({
    acceptFn,
    cancelFn,
  });

  const isSubmitting = methods.formState.isSubmitting;
  const isSending =
    isSubmitting || !!sendProposalState.loading || !!sendProposalPackageState.loading;
  const isQueueingPackageSend =
    methods.watch("sendToMultipleCompanies") &&
    (isSubmitting || !!sendProposalPackageState.loading);
  const productsWithPendingChanges = Object.keys(productsFormsStatus).filter(
    (productId) => productsFormsStatus[productId]?.isDirty
  );
  const hasDirtyProducts = productsWithPendingChanges.length > 0;

  return (
    <UiCenterModal
      open={open}
      onClose={onClose}
      title="Confirm proposal details"
      footer={
        <MDBox display="flex" gap="8px" ml="auto">
          <PreviewProposalButton
            proposal={proposal}
            disabled={isSending || hasDirtyProducts}
            buildVariables={buildPreviewVarsFromForm}
          />
          <UiButton
            uiVariant="primary-green"
            onClick={methods.handleSubmit((data) => handleSend(data))}
            disabled={isSending || hasDirtyProducts}
          >
            {isQueueingPackageSend
              ? "Queueing proposal send..."
              : isSending
              ? "Sending..."
              : "Send Proposal"}
          </UiButton>
        </MDBox>
      }
    >
      <FormProvider {...methods}>
        <UiCard variant="modal-section">
          <UiTypography uiVariant="overline-lg" sx={sectionHeaderSx}>
            Client
          </UiTypography>
          <MDBox display="grid" gridTemplateColumns="160px 1fr" rowGap="8px" alignItems="center">
            <UiTypography uiVariant="label">Company</UiTypography>
            <UiTypography uiVariant="body" sx={{ color: "#212936" }}>
              {proposal.company?.name ?? "N/A"}
            </UiTypography>
            <UiTypography uiVariant="label">Contact</UiTypography>
            <UiTypography uiVariant="body" sx={{ color: "#212936" }}>
              {contactName}
            </UiTypography>
          </MDBox>
        </UiCard>

        <UiCard variant="modal-section">
          <UiTypography uiVariant="overline-lg" sx={sectionHeaderSx}>
            Settings
          </UiTypography>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <Controller
              name="removeUnitPriceAndQuantity"
              control={methods.control}
              render={({ field }) => (
                <MDBox display="flex" alignItems="center">
                  <Checkbox
                    size="small"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                  <UiTypography uiVariant="body-sm">Remove unit price and quantity</UiTypography>
                </MDBox>
              )}
            />
            <Controller
              name="removeIndividualLineItemPricing"
              control={methods.control}
              render={({ field }) => (
                <MDBox display="flex" alignItems="center">
                  <Checkbox
                    size="small"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                  <UiTypography uiVariant="body-sm">
                    Remove individual line item pricing
                  </UiTypography>
                </MDBox>
              )}
            />
          </MDBox>
          <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap="12px">
            <MDBox
              display="flex"
              flexDirection="column"
              gap="4px"
              p="6px 8px 6px"
              borderRadius="8px"
              border={`1px solid ${draftColors.border}`}
            >
              <UiTypography uiVariant="label-xs">Proposal format</UiTypography>
              <MDBox sx={confirmProposalDropdownInnerSx}>
                <CustomSelect
                  control={methods.control}
                  fullWidth
                  selectVariant="standard"
                  data={enumToValueOptions(ProposalLayout)}
                  {...methods.register("layout")}
                  label=""
                />
              </MDBox>
            </MDBox>
            {allDocuments.length > 0 && (
              <MDBox
                display="flex"
                flexDirection="column"
                gap="4px"
                p="6px 8px 6px"
                borderRadius="8px"
                border={`1px solid ${draftColors.border}`}
              >
                <UiTypography uiVariant="label-xs">Proposal attachments</UiTypography>
                <MDBox sx={confirmProposalDropdownInnerSx}>
                  <Controller
                    name="attachments"
                    control={methods.control}
                    render={({ field }) => (
                      <CustomMultipleSelect
                        {...field}
                        fullWidth
                        onChange={(e) => field.onChange(e?.map((x) => x.value as string))}
                        label=""
                        value={
                          (field.value as string[])?.map((s) => ({
                            label: getDocumentLabel(s),
                            value: s,
                          })) ?? []
                        }
                        options={allDocuments.map((d) => ({
                          value: d.id,
                          label: getDocumentLabel(d.id),
                        }))}
                      />
                    )}
                  />
                </MDBox>
              </MDBox>
            )}
          </MDBox>
        </UiCard>

        <UiCard variant="modal-section">
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb="12px">
            <UiTypography uiVariant="overline-lg" sx={sectionHeaderSx}>
              Recipients
            </UiTypography>
            <Controller
              name="sendToMultipleCompanies"
              control={methods.control}
              render={({ field }) => (
                <MDBox display="flex" alignItems="center">
                  <Checkbox
                    size="small"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                  <UiTypography uiVariant="body-sm">
                    Send to Multiple Companies
                  </UiTypography>
                </MDBox>
              )}
            />
          </MDBox>
          <Recipients />
          <PackageRecipients enabled={open} />
        </UiCard>

        <UiCard variant="modal-section">
          <UiTypography uiVariant="overline-lg" sx={sectionHeaderSx}>
            Email settings
          </UiTypography>
          <UiFieldGroup
            label="Email subject"
            value=""
            inputProps={methods.register("emailSubjectOverride")}
          />
          <Controller
            control={methods.control}
            name="emailBodyOverride"
            render={({ field, fieldState: { error } }) => (
              <>
                <WYSIWYG
                  type="proposal"
                  content={field.value ?? ""}
                  onChange={(content) => field.onChange(content)}
                  title="Email body"
                  tooltip="This text will be included in the email when the proposal is sent."
                  onBlur={field.onBlur}
                  config={{
                    removePlugins: [
                      "EasyImage",
                      "ImageUpload",
                      "MediaEmbed",
                      "Table",
                      "TableToolbar",
                    ],
                  }}
                />
                {error && <FormHelperText sx={{ color: "red" }}>{error.message}</FormHelperText>}
              </>
            )}
          />
        </UiCard>

        {proposalSectionOptions.length > 0 && (
          <UiCard variant="modal-section">
            <UiTypography uiVariant="overline-lg" sx={sectionHeaderSx}>
              Attached sections
            </UiTypography>
            <MDBox
              display="flex"
              flexDirection="column"
              gap="4px"
              p="6px 8px 6px"
              borderRadius="8px"
              border={`1px solid ${draftColors.border}`}
            >
              <MDBox sx={confirmProposalDropdownInnerSx}>
                <Controller
                  name="contentSettingIds"
                  control={methods.control}
                  render={({ field }) => (
                    <CustomMultipleSelect
                      {...field}
                      fullWidth
                      onChange={(options) =>
                        field.onChange((options ?? []).map((o) => o.value as string))
                      }
                      label=""
                      value={
                        (field.value as string[])?.map((id) => {
                          const opt = proposalSectionOptions.find((o) => o.value === id);
                          return opt ?? { label: id, value: id };
                        }) ?? []
                      }
                      options={proposalSectionOptions}
                    />
                  )}
                />
              </MDBox>
            </MDBox>
          </UiCard>
        )}
      </FormProvider>
      <ActionConfirmDialog
        open={confirmDialogOpen}
        setOpen={setConfirmDialogOpen}
        handleAccept={onAccept}
        handleCancel={onCancel}
        title="Confirmation"
        content={
          jobSizeError?.replace("Please input a job size.", "Proceed?") ||
          "Are you sure you want to proceed?"
        }
      />
    </UiCenterModal>
  );
}
